import ChatBubbleClusterFileUrl from '@capturi/assets/images/ChatBubbleCluster.svg'
import ColoredBackgroundFileUrl from '@capturi/assets/images/ColoredBackgroundTop.svg'
import PuzzelFlowerFileUrl from '@capturi/assets/images/PuzzelFlower.svg'
import PuzzelLogoFileUrl from '@capturi/assets/images/PuzzelLogo.svg'
import ChordFileUrl from '@capturi/assets/images/chord.svg'
import LogoText_colored from '@capturi/assets/images/logo/puzzel_conversational_intelligence.svg'
import { useModal } from '@capturi/use-modal'
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  BoxProps,
  Flex,
  Text,
} from '@chakra-ui/react'
import { css } from '@emotion/react'
import { Trans } from '@lingui/macro'
import React, { useEffect, useState } from 'react'

import { Variants, motion } from 'framer-motion'
import { useSearchParams } from 'react-router'

import { EmailLogin } from './EmailLogin'
import { LanguagesList } from './LanguagesList'
import { NoAccessModal } from './NoAccessModal'
import { ProviderButtons } from './ProviderButtons'

const backgroundAnimation: Variants = {
  hidden: { y: '-100%', opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: { duration: 0.8, ease: 'easeOut', delay: 0.5 },
  },
}

const puzzelAnimation: Variants = {
  hidden: { y: '100%', opacity: 0, rotate: -85 },
  visible: {
    y: 0,
    opacity: 1,
    rotate: 0,
    transition: { duration: 0.8, ease: 'easeOut', delay: 0.7 },
  },
}

const chatBubbleAnimation: Variants = {
  hidden: { y: '100%', opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: { duration: 0.8, ease: 'easeOut', delay: 1.2 },
  },
}

const chordAnimation: Variants = {
  hidden: { x: '-100%', opacity: 0 },
  visible: {
    x: 0,
    opacity: 1,
    transition: { duration: 0.8, ease: 'easeOut', delay: 1.4 },
  },
}

const LoginPage: React.FC = () => {
  const [searchParams] = useSearchParams()
  const message = searchParams.get('message') ?? ''
  const [error, setError] = useState<string | null>(null)
  const [knownOrg, setKnownOrg] = useState<boolean>(true)

  const [openNoAccessModal] = useModal(NoAccessModal)

  useEffect(() => {
    if (message.toLowerCase() === 'no invite code') {
      openNoAccessModal()
    } else {
      setError(message)
    }
  }, [message, openNoAccessModal])

  return (
    <Flex
      h="100%"
      overflowY="scroll"
      justify="center"
      css={css`
        &::-webkit-scrollbar {
          display: none;
        }
      `}
    >
      <WavesBackground />
      <LoginArea
        w={{ base: '100%', md: '48em' }}
        minH="650px"
        h="100vh"
        as="main"
      >
        <Flex
          align="center"
          direction="column"
          w="100%"
          h="100%"
          justify="center"
        >
          <Box
            flex="0 0 auto"
            filter="drop-shadow(0px 1px 1px rgba(0, 0, 0, 0.10)) drop-shadow(0px 2px 2px rgba(0, 0, 0, 0.09)) drop-shadow(0px 5px 3px rgba(0, 0, 0, 0.05)) drop-shadow(0px 10px 4px rgba(0, 0, 0, 0.01)) drop-shadow(0px 15px 4px rgba(0, 0, 0, 0.00))"
          >
            <LogoText_colored width="200px" color="greys.600" />
          </Box>
          <LoginCard w="100%" maxW="30em" mt="8">
            <Text fontWeight="semibold" textAlign="center" fontSize="2xl">
              <Trans>Log in to your organization</Trans>
            </Text>

            <ProviderButtons mt={8} knownOrg={knownOrg} />
            <EmailLogin setKnownOrg={setKnownOrg} />
            {error && (
              <Alert status="error" mt={4}>
                <AlertIcon />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
          </LoginCard>
          <Box aria-hidden width="90px" pt={8} mx="auto">
            <PuzzelLogoFileUrl />
          </Box>
        </Flex>
      </LoginArea>

      <Flex
        pos="absolute"
        bottom={0}
        mb={4}
        wrap="nowrap"
        w="100%"
        justify="center"
      >
        <LanguagesList />
      </Flex>
    </Flex>
  )
}

const LoginCard: React.FC<BoxProps> = (props) => {
  return (
    <Box
      bg="workspace.contentBackground"
      border="1px solid"
      borderColor="workspace.contentBorder"
      backdropFilter="blur(8px)"
      boxShadow="glassShadow"
      mt={4}
      borderRadius={8}
      px={6}
      py={8}
      {...props}
    />
  )
}

const LoginArea: React.FC<BoxProps> = ({ children, ...props }) => {
  return (
    <Flex pos="relative" px={[2, 4, 8]} pb={8} {...props}>
      <Box w="100%" zIndex={1}>
        {children}
      </Box>
    </Flex>
  )
}

const WavesBackground: React.FC = () => {
  return (
    <Box pos="absolute" bottom={0} top={0} left={0} right={0}>
      <Box
        as={motion.div}
        pos="absolute"
        top={0}
        left={0}
        right={0}
        aria-hidden
        width="100%"
        variants={backgroundAnimation}
        initial="hidden"
        animate="visible"
      >
        <ColoredBackgroundFileUrl />
      </Box>
      <Box
        as={motion.div}
        pos="absolute"
        bottom={0}
        right={0}
        aria-hidden
        width="20vw"
        maxWidth="400px"
        variants={puzzelAnimation}
        initial="hidden"
        animate="visible"
      >
        <PuzzelFlowerFileUrl />
      </Box>
      <Box
        as={motion.div}
        pos="absolute"
        bottom={0}
        left={0}
        aria-hidden
        width="20vw"
        maxWidth="400px"
        variants={chatBubbleAnimation}
        initial="hidden"
        animate="visible"
      >
        <ChatBubbleClusterFileUrl />
      </Box>
      <Box
        as={motion.div}
        pos="absolute"
        top={0}
        left={0}
        aria-hidden
        width="20vw"
        maxWidth="400px"
        variants={chordAnimation}
        initial="hidden"
        animate="visible"
      >
        <ChordFileUrl />
      </Box>
    </Box>
  )
}

export default LoginPage
